import React, { useState, useEffect } from 'react';
import { formatInTenantTz } from '../../../lib/datetime';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Progress } from '../../ui/progress';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  Brain,
  BookOpen,
  TrendingUp,
  ThumbsUp,
  ThumbsDown,
  Star,
  Clock,
  Target,
  Zap,
  AlertTriangle,
  CheckCircle,
  Play,
  Pause,
  Save,
  Upload,
  Download,
  MessageSquare,
  Lightbulb,
  Award
} from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';

const LearningCenter = () => {
  const { currentBusiness } = useBusiness();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [trainingActive, setTrainingActive] = useState(false);
  
  // Dialog states
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);
  
  // Form states
  const [newTrainingScenario, setNewTrainingScenario] = useState({
    title: '',
    scenario: '',
    expectedResponse: '',
    category: 'general',
    difficulty: 'medium',
    tags: ''
  });

  const [feedbackData, setFeedbackData] = useState({
    conversationId: '',
    rating: 5,
    feedback: '',
    suggestions: ''
  });

  // Phase 5 — REAL Learning Center data from GET /ai-agent/learning/{business_id}.
  // No local model is trained (hosted LLM), so model-performance metrics stay null/honest and the
  // backend returns a not_tracked reason. Real signals surfaced: AI feedback + Solution-Bank counts.
  const [learningData, setLearningData] = useState({
    modelPerformance: {
      accuracy: null,
      learningRate: null,
      trainingProgress: null,
      lastTraining: null,
      status: null,
      reason: null
    },
    solutionLearning: { pendingAiSolutions: 0, approvedSolutions: 0 },
    recentFeedback: [],
    feedbackCount: 0,
    trainingScenarios: [],
    learningOpportunities: []
  });

  useEffect(() => {
    const bizId = currentBusiness?.brandId;
    if (!bizId) { setLoading(false); return; }
    let cancelled = false;
    const fetchLearning = async () => {
      setLoading(true);
      try {
        const resp = await makeAuthenticatedRequest(`/api/v1/ai-agent/learning/${encodeURIComponent(bizId)}`);
        if (!resp.ok) { if (!cancelled) setLoading(false); return; }
        const d = await resp.json();
        if (cancelled) return;
        const mp = d.model_performance || {};
        const sl = d.solution_learning || {};
        setLearningData({
          modelPerformance: {
            accuracy: mp.accuracy ?? null,
            learningRate: mp.learning_rate ?? null,
            trainingProgress: mp.training_progress ?? null,
            lastTraining: mp.last_training ?? null,
            status: mp.status ?? null,
            reason: mp.reason ?? null
          },
          solutionLearning: {
            pendingAiSolutions: sl.pending_ai_solutions ?? 0,
            approvedSolutions: sl.approved_solutions ?? 0
          },
          recentFeedback: (Array.isArray(d.recent_feedback) ? d.recent_feedback : []).map((f) => ({
            id: f.conversation_id,
            conversationId: f.conversation_id,
            helpful: f.resolution_helpful === true,
            rating: typeof f.customer_satisfaction === 'number' ? f.customer_satisfaction : null,
            category: 'AI resolution',
            timestamp: f.submitted_at,
            feedback: f.feedback_text || ''
          })),
          feedbackCount: d.feedback_count || 0,
          trainingScenarios: Array.isArray(d.training_scenarios) ? d.training_scenarios : [],
          learningOpportunities: []
        });
      } catch (e) {
        // network/parse failure — keep honest-empty defaults, no fake data
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLearning();
    return () => { cancelled = true; };
  }, [currentBusiness?.brandId, makeAuthenticatedRequest]);

  const handleStartTraining = () => {
    // No AI training endpoint exists yet — do not simulate a training run or fake progress.
    // real wiring lands in the AI backend phase
    toast({
      title: t('ak.notAvailableYet'),
      description: t('ak.trainingUnavailableDesc'),
    });
  };

  const handleSubmitScenario = () => {
    if (!newTrainingScenario.title || !newTrainingScenario.scenario || !newTrainingScenario.expectedResponse) {
      toast({
        title: t('ak.missingInformation'),
        description: t('ak.fillRequiredFields'),
        variant: "destructive",
      });
      return;
    }

    // No training-scenario endpoint exists yet — do not add a fake entry to a mock queue.
    // real wiring lands in the AI backend phase
    setNewTrainingScenario({
      title: '',
      scenario: '',
      expectedResponse: '',
      category: 'general',
      difficulty: 'medium',
      tags: ''
    });

    setScenarioDialogOpen(false);

    toast({
      title: t('ak.notAvailableYet'),
      description: t('ak.saveScenariosUnavailableDesc'),
    });
  };

  const handleSubmitFeedback = () => {
    if (!feedbackData.feedback) {
      toast({
        title: t('ak.missingFeedback'),
        description: t('ak.provideFeedbackContent'),
        variant: "destructive",
      });
      return;
    }

    // Submitting AI learning feedback is not wired to a backend here yet — no fake local append.
    // real wiring lands in the AI backend phase
    setFeedbackData({
      conversationId: '',
      rating: 5,
      feedback: '',
      suggestions: ''
    });

    setFeedbackDialogOpen(false);

    toast({
      title: t('ak.notAvailableYet'),
      description: t('ak.submitFeedbackUnavailableDesc'),
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      case 'expert': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex-1 h-full flex items-center justify-center bg-gradient-to-br from-white via-blue-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('ak.loadingLearningCenter')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-blue-50 to-purple-50">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-600">
              <Brain className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('ak.learningCenterTitle')}</h1>
              <p className="text-gray-600">{t('ak.learningCenterSubtitle')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Badge className={`${trainingActive ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
              {trainingActive ? t('ak.trainingActive') : t('ak.ready')}
            </Badge>

            {!trainingActive ? (
              <Button
                onClick={handleStartTraining}
                className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
              >
                <Play className="h-4 w-4 mr-2" />
                {t('ak.startTraining')}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setTrainingActive(false)}>
                <Pause className="h-4 w-4 mr-2" />
                {t('ak.pauseTraining')}
              </Button>
            )}
          </div>
        </div>

        {/* Training Progress */}
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.modelAccuracy')}</p>
                  <p className="text-2xl font-bold text-purple-600">{learningData.modelPerformance.accuracy != null ? `${learningData.modelPerformance.accuracy}%` : '—'}</p>
                </div>
                <Target className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.learningRate')}</p>
                  <p className="text-2xl font-bold text-blue-600">{learningData.modelPerformance.learningRate != null ? learningData.modelPerformance.learningRate : '—'}</p>
                </div>
                <Zap className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.trainingProgress')}</p>
                  <p className="text-2xl font-bold text-green-600">{learningData.modelPerformance.trainingProgress != null ? `${learningData.modelPerformance.trainingProgress}%` : '—'}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('ak.lastTraining')}</p>
                  <p className="text-sm font-bold text-orange-600">
                    {learningData.modelPerformance.lastTraining
                      ? formatInTenantTz(learningData.modelPerformance.lastTraining, { dateStyle: 'medium' })
                      : '—'}
                  </p>
                  {learningData.modelPerformance.lastTraining && (
                    <p className="text-xs text-gray-500">
                      {formatInTenantTz(learningData.modelPerformance.lastTraining, { timeStyle: 'medium' })}
                    </p>
                  )}
                </div>
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs defaultValue="feedback" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="feedback">{t('ak.tab.customerFeedback')}</TabsTrigger>
          <TabsTrigger value="training">{t('ak.tab.trainingScenarios')}</TabsTrigger>
          <TabsTrigger value="opportunities">{t('ak.tab.learningOpportunities')}</TabsTrigger>
          <TabsTrigger value="performance">{t('ak.tab.modelPerformance')}</TabsTrigger>
        </TabsList>

        {/* Customer Feedback Tab */}
        <TabsContent value="feedback" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">{t('ak.recentCustomerFeedback')}</h2>
            <Dialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  {t('ak.addFeedback')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{t('ak.submitAiFeedback')}</DialogTitle>
                  <DialogDescription>
                    {t('ak.submitAiFeedbackDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">{t('ak.conversationIdOptional')}</label>
                    <Input
                      placeholder={t('ak.enterConversationId')}
                      value={feedbackData.conversationId}
                      onChange={(e) => setFeedbackData(prev => ({ ...prev, conversationId: e.target.value }))}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">{t('ak.rating')}</label>
                    <div className="flex items-center space-x-2 mt-1">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          onClick={() => setFeedbackData(prev => ({ ...prev, rating }))}
                          className={`p-1 ${feedbackData.rating >= rating ? 'text-yellow-500' : 'text-gray-300'}`}
                        >
                          <Star className="h-6 w-6 fill-current" />
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-gray-600">{feedbackData.rating}/5</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">{t('ak.feedbackRequired')}</label>
                    <Textarea
                      placeholder={t('ak.feedbackPlaceholder')}
                      value={feedbackData.feedback}
                      onChange={(e) => setFeedbackData(prev => ({ ...prev, feedback: e.target.value }))}
                      rows={4}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">{t('ak.suggestionsForImprovement')}</label>
                    <Textarea
                      placeholder={t('ak.suggestionsPlaceholder')}
                      value={feedbackData.suggestions}
                      onChange={(e) => setFeedbackData(prev => ({ ...prev, suggestions: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setFeedbackDialogOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleSubmitFeedback}>
                      <Save className="h-4 w-4 mr-2" />
                      {t('ak.submitFeedback')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-4">
            {learningData.recentFeedback.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p>{t('ak.noFeedbackYet')}</p>
                <p className="text-sm text-gray-400">{t('ak.noFeedbackDesc')}</p>
              </div>
            )}
            {learningData.recentFeedback.map((feedback) => (
              <Card key={feedback.id} className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        {feedback.helpful ? (
                          <ThumbsUp className="h-4 w-4 text-green-500" />
                        ) : (
                          <ThumbsDown className="h-4 w-4 text-red-500" />
                        )}
                        {typeof feedback.rating === 'number' && (
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-3 w-3 ${star <= feedback.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      <Badge variant="outline">{feedback.category}</Badge>
                      {!feedback.helpful && (
                        <Badge className="bg-red-100 text-red-800">
                          <AlertTriangle className="h-3 w-3 mr-1 inline" />
                          {t('ak.needsReview')}
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatInTenantTz(feedback.timestamp)}
                    </span>
                  </div>
                  <p className="text-gray-700 mb-2">{feedback.feedback}</p>
                  <div className="text-xs text-gray-500">
                    {t('ak.conversationLabel')} {feedback.conversationId}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Training Scenarios Tab */}
        <TabsContent value="training" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">{t('ak.tab.trainingScenarios')}</h2>
            <div className="flex items-center space-x-2">
              <Dialog open={trainingDialogOpen} onOpenChange={setTrainingDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Upload className="h-4 w-4 mr-2" />
                    {t('ak.importScenarios')}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('ak.importTrainingScenarios')}</DialogTitle>
                    <DialogDescription>{t('ak.importScenariosDesc')}</DialogDescription>
                  </DialogHeader>
                  <div className="p-6 text-center text-sm text-gray-500">
                    {t('ak.importDropHint')}
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={scenarioDialogOpen} onOpenChange={setScenarioDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                    <BookOpen className="h-4 w-4 mr-2" />
                    {t('ak.addScenario')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t('ak.createTrainingScenario')}</DialogTitle>
                    <DialogDescription>
                      {t('ak.createTrainingScenarioDesc')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">{t('ak.scenarioTitleRequired')}</label>
                        <Input
                          placeholder={t('ak.enterScenarioTitle')}
                          value={newTrainingScenario.title}
                          onChange={(e) => setNewTrainingScenario(prev => ({ ...prev, title: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">{t('ak.category')}</label>
                        <Select
                          value={newTrainingScenario.category}
                          onValueChange={(value) => setNewTrainingScenario(prev => ({ ...prev, category: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">{t('ak.scenarioCatGeneral')}</SelectItem>
                            <SelectItem value="billing">{t('ak.cat.billing')}</SelectItem>
                            <SelectItem value="technical">{t('ak.cat.technical')}</SelectItem>
                            <SelectItem value="account">{t('ak.cat.account')}</SelectItem>
                            <SelectItem value="product">{t('ak.cat.product')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">{t('ak.customerScenarioRequired')}</label>
                      <Textarea
                        placeholder={t('ak.customerScenarioPlaceholder')}
                        value={newTrainingScenario.scenario}
                        onChange={(e) => setNewTrainingScenario(prev => ({ ...prev, scenario: e.target.value }))}
                        rows={4}
                      />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">{t('ak.expectedAiResponseRequired')}</label>
                      <Textarea
                        placeholder={t('ak.expectedAiResponsePlaceholder')}
                        value={newTrainingScenario.expectedResponse}
                        onChange={(e) => setNewTrainingScenario(prev => ({ ...prev, expectedResponse: e.target.value }))}
                        rows={4}
                      />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">{t('ak.difficultyLevel')}</label>
                        <Select
                          value={newTrainingScenario.difficulty}
                          onValueChange={(value) => setNewTrainingScenario(prev => ({ ...prev, difficulty: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="easy">{t('ak.difficulty.easy')}</SelectItem>
                            <SelectItem value="medium">{t('ak.difficulty.medium')}</SelectItem>
                            <SelectItem value="hard">{t('ak.difficulty.hard')}</SelectItem>
                            <SelectItem value="expert">{t('ak.difficulty.expert')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">{t('ak.tags')}</label>
                        <Input
                          placeholder={t('ak.tagsCommaPlaceholder')}
                          value={newTrainingScenario.tags}
                          onChange={(e) => setNewTrainingScenario(prev => ({ ...prev, tags: e.target.value }))}
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-2 pt-4">
                      <Button variant="outline" onClick={() => setScenarioDialogOpen(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button onClick={handleSubmitScenario}>
                        <Save className="h-4 w-4 mr-2" />
                        {t('ak.addScenario')}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="space-y-4">
            {learningData.trainingScenarios.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p>{t('ak.noScenariosYet')}</p>
                <p className="text-sm text-gray-400">{t('ak.noScenariosDesc')}</p>
              </div>
            )}
            {learningData.trainingScenarios.map((scenario) => (
              <Card key={scenario.id} className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900">{scenario.title}</h3>
                      <Badge variant="outline">{scenario.category}</Badge>
                      <Badge className={getDifficultyColor(scenario.difficulty)}>
                        {scenario.difficulty}
                      </Badge>
                      <Badge className={scenario.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {scenario.status}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      <span className="text-sm font-semibold">{scenario.success_rate}%</span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">{t('ak.scenarioLabel')}</h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{scenario.scenario}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">{t('ak.expectedResponseLabel')}</h4>
                      <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded">{scenario.expectedResponse}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Learning Opportunities Tab */}
        <TabsContent value="opportunities" className="space-y-6">
          <h2 className="text-xl font-semibold">{t('ak.tab.learningOpportunities')}</h2>

          <div className="space-y-4">
            {learningData.learningOpportunities.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Lightbulb className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p>{t('ak.noOpportunitiesYet')}</p>
                <p className="text-sm text-gray-400">{t('ak.noOpportunitiesDesc')}</p>
              </div>
            )}
            {learningData.learningOpportunities.map((opportunity) => (
              <Card key={opportunity.id} className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-full bg-blue-100">
                        <Lightbulb className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{opportunity.title}</h3>
                        <p className="text-sm text-gray-600">{opportunity.description}</p>
                      </div>
                    </div>
                    <Badge className={getPriorityColor(opportunity.priority)}>
                      {opportunity.priority} {t('ak.prioritySuffix')}
                    </Badge>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded">
                      <h4 className="text-sm font-medium text-gray-700 mb-1">{t('ak.recommendedAction')}</h4>
                      <p className="text-sm text-gray-600">{opportunity.action}</p>
                    </div>

                    <div className="p-3 bg-green-50 rounded">
                      <h4 className="text-sm font-medium text-green-700 mb-1">{t('ak.expectedImpact')}</h4>
                      <p className="text-sm text-green-600">{opportunity.impact}</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex space-x-2">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {t('ak.implement')}
                    </Button>
                    <Button size="sm" variant="outline">
                      <Clock className="h-3 w-3 mr-1" />
                      {t('ak.schedule')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Model Performance Tab */}
        <TabsContent value="performance" className="space-y-6">
          <h2 className="text-xl font-semibold">{t('ak.modelPerformanceAnalytics')}</h2>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Brain className="h-5 w-5 text-purple-600" />
                  <span>{t('ak.aiModelPerformance')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{t('ak.modelAccuracy')}</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-purple-600">{learningData.modelPerformance.accuracy != null ? `${learningData.modelPerformance.accuracy}%` : '—'}</span>
                      <Target className="h-4 w-4 text-purple-500" />
                    </div>
                  </div>
                  <Progress value={learningData.modelPerformance.accuracy || 0} className="h-3" />

                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{t('ak.learningProgress')}</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-blue-600">{learningData.modelPerformance.trainingProgress != null ? `${learningData.modelPerformance.trainingProgress}%` : '—'}</span>
                      <TrendingUp className="h-4 w-4 text-blue-500" />
                    </div>
                  </div>
                  <Progress value={learningData.modelPerformance.trainingProgress || 0} className="h-3" />

                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h4 className="font-medium text-purple-800 mb-2">{t('ak.trainingStatus')}</h4>
                    <p className="text-sm text-purple-700">
                      {learningData.modelPerformance.reason
                        || t('ak.trainingStatusFallback')}
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <div className="text-center">
                        <p className="text-xl font-bold text-purple-700">{learningData.solutionLearning.pendingAiSolutions}</p>
                        <p className="text-xs text-purple-600">{t('ak.aiSolutionsPending')}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl font-bold text-green-700">{learningData.solutionLearning.approvedSolutions}</p>
                        <p className="text-xs text-green-600">{t('ak.approvedIntoSolutionBank')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Award className="h-5 w-5 text-orange-600" />
                  <span>{t('ak.trainingAchievements')}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Training achievements were fabricated (94.2% / 1,200+ / <2s) — replaced with an
                    honest empty state until real training telemetry exists.
                    real wiring lands in the AI backend phase */}
                <div className="space-y-4">
                  <div className="text-center py-8 text-gray-500">
                    <Award className="h-12 w-12 mx-auto mb-3 opacity-40" />
                    <p>{t('ak.noAchievementsYet')}</p>
                    <p className="text-sm text-gray-400">{t('ak.noAchievementsDesc')}</p>
                  </div>

                  <div className="text-center pt-4">
                    <Button
                      className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                      onClick={() => toast({
                        title: t('ak.notAvailableYet'),
                        description: t('ak.exportReportUnavailableDesc'),
                      })}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {t('ak.exportPerformanceReport')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LearningCenter;